import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { InstructorWithProfile } from '@/types/database'

const favKeys = {
  all: ['favorites'] as const,
  ids: (userId: string) => [...favKeys.all, 'ids', userId] as const,
  list: (userId: string) => [...favKeys.all, 'list', userId] as const,
}

async function fetchFavoriteIds(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('student_favorites')
    .select('instructor_id')
    .eq('student_id', userId)

  if (error) throw new Error(error.message)
  return (data ?? []).map((r) => r.instructor_id)
}

async function fetchFavoriteInstructors(userId: string): Promise<InstructorWithProfile[]> {
  const ids = await fetchFavoriteIds(userId)
  if (ids.length === 0) return []

  const { data, error } = await supabase
    .from('instructors')
    .select('*, profile:profiles(*), specialties:instructor_specialties(specialty)')
    .in('id', ids)
    .eq('status', 'approved')
    .order('rating_avg', { ascending: false })

  if (error) throw new Error(error.message)
  return (data ?? []) as unknown as InstructorWithProfile[]
}

export function useFavoriteIds() {
  const { profile } = useAuth()
  const userId = profile?.id ?? ''

  return useQuery<string[], Error>({
    queryKey: favKeys.ids(userId),
    queryFn: () => fetchFavoriteIds(userId),
    enabled: Boolean(userId),
    staleTime: 1000 * 60 * 2,
  })
}

export function useFavorites() {
  const { profile } = useAuth()
  const userId = profile?.id ?? ''

  return useQuery<InstructorWithProfile[], Error>({
    queryKey: favKeys.list(userId),
    queryFn: () => fetchFavoriteInstructors(userId),
    enabled: Boolean(userId),
    staleTime: 1000 * 60 * 2,
  })
}

export function useToggleFavorite() {
  const { profile } = useAuth()
  const queryClient = useQueryClient()
  const userId = profile?.id ?? ''

  return useMutation<void, Error, { instructorId: string; isFav: boolean }>({
    mutationFn: async ({ instructorId, isFav }) => {
      if (isFav) {
        const { error } = await supabase
          .from('student_favorites')
          .delete()
          .eq('student_id', userId)
          .eq('instructor_id', instructorId)
        if (error) throw new Error(error.message)
      } else {
        const { error } = await supabase
          .from('student_favorites')
          .insert({ student_id: userId, instructor_id: instructorId })
        if (error) throw new Error(error.message)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: favKeys.all })
    },
  })
}
